import { Platform } from 'react-native';

import {apiLink} from '../Common/common';

type SSEListener = (data: any) => void;

class SSEClient {
  private eventSource: any = null;
  private xhr: XMLHttpRequest | null = null;
  private listeners: Set<SSEListener> = new Set();
  private company: string | null = null;
  private token: string | null = null;
  private reconnectTimeout: any = null;
  private watchdogInterval: any = null;
  private lastPingTime = Date.now();
  private buffer = '';

  connect(company: string, token?: string) {
    if (
      (this.eventSource || this.xhr) &&
      this.company === company &&
      this.token === token
    ) {
      return;
    }

    this.company = company;
    this.token = token || null;
    this.disconnect();
    this.lastPingTime = Date.now();
    this.startWatchdog();

    let url = `${apiLink}events/stream?company=${encodeURIComponent(company)}`;
    if (token) {
      url += `&token=${encodeURIComponent(token)}`;
    }

    if (typeof EventSource !== 'undefined') {
      try {
        this.eventSource = new EventSource(url);

        this.eventSource.onmessage = (event: any) => {
          this.lastPingTime = Date.now();
          this.handleRawData(event.data);
        };

        this.eventSource.onerror = (err: any) => {
          console.warn('SSE EventSource Warning/Error:', err);
          this.scheduleReconnect(company, token);
        };
      } catch (error) {
        console.error('Failed to create EventSource, falling back to XHR stream:', error);
        this.connectViaXHR(url, company, token);
      }
    } else {
      this.connectViaXHR(url, company, token);
    }
  }

  private connectViaXHR(url: string, company: string, token?: string) {
    try {
      this.xhr = new XMLHttpRequest();
      this.buffer = '';

      this.xhr.open('GET', url, true);
      this.xhr.setRequestHeader('Accept', 'text/event-stream');
      this.xhr.setRequestHeader('Cache-Control', 'no-cache');
      if (token) {
        this.xhr.setRequestHeader('auth', token);
      }

      this.xhr.onreadystatechange = () => {
        if (this.xhr && (this.xhr.readyState === 3 || this.xhr.readyState === 4)) {
          this.processXHRBuffer();
        }
        if (this.xhr && this.xhr.readyState === 4) {
          this.scheduleReconnect(company, token);
        }
      };

      this.xhr.onerror = (err) => {
        console.warn('SSE XHR Stream Error:', err);
        this.scheduleReconnect(company, token);
      };

      this.xhr.send();
    } catch (err) {
      console.error('Failed to start SSE XHR connection:', err);
      this.scheduleReconnect(company, token);
    }
  }

  private processXHRBuffer() {
    if (!this.xhr) return;
    const responseText = this.xhr.responseText || '';
    const newChunk = responseText.slice(this.buffer.length);
    this.buffer = responseText;

    // Process event blocks delimited by double newlines or single lines
    const lines = newChunk.split(/\r?\n/);
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('data:')) {
        this.lastPingTime = Date.now();
        const rawJson = trimmed.slice(5).trim();
        this.handleRawData(rawJson);
      } else if (trimmed.startsWith(':ping') || trimmed.startsWith(':')) {
        this.lastPingTime = Date.now();
      }
    }
  }

  private handleRawData(rawJson: string) {
    if (!rawJson) return;
    try {
      const data = JSON.parse(rawJson);
      this.listeners.forEach(listener => listener(data));
    } catch (e) {
      console.error('Error parsing SSE payload:', e, 'Payload:', rawJson);
    }
  }

  private startWatchdog() {
    this.stopWatchdog();
    this.watchdogInterval = setInterval(() => {
      // If no data or ping received for over 35 seconds (2 missed 15s heartbeats), trigger reconnection
      if (Date.now() - this.lastPingTime > 35000) {
        if (this.company) {
          console.warn('SSE Stream Keep-Alive missed (35s). Reconnecting...');
          this.scheduleReconnect(this.company, this.token || undefined);
        }
      }
    }, 10000);
  }

  private stopWatchdog() {
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
  }

  private scheduleReconnect(company: string, token?: string) {
    this.disconnect();
    if (!this.reconnectTimeout) {
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectTimeout = null;
        this.connect(company, token);
      }, 5000);
    }
  }

  disconnect() {
    this.stopWatchdog();
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch {}
      this.eventSource = null;
    }
    if (this.xhr) {
      try {
        this.xhr.abort();
      } catch {}
      this.xhr = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.buffer = '';
  }

  addListener(listener: SSEListener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export const sseClient = new SSEClient();
